export type DashboardFilterKind =
  | "text"
  | "select"
  | "multiselect"
  | "status"
  | "tags"
  | "daterange";

export interface DashboardFilterOption {
  value: string;
  label: string;
}

export interface DashboardFilterConfig {
  field: string;
  label: string;
  kind: DashboardFilterKind;
  options?: DashboardFilterOption[];
}

export type FilterAccessor<T> = (item: T, field: string) => string | string[];

/**
 * Custom per-field filter logic, keyed by filter `field`. Overrides the
 * default value-membership match — used for ranges/dates/anything not
 * expressible as "value ∈ selected". Receives the item and the selected values.
 */
export type FilterPredicate<T> = (item: T, values: string[]) => boolean;
export type FilterPredicates<T> = Record<string, FilterPredicate<T>>;

export function defaultFilterAccessor<T>(
  item: T,
  field: string,
): string | string[] {
  const value = (item as Record<string, unknown>)[field];
  if (Array.isArray(value)) return value.map((v) => String(v));
  return value == null ? "" : String(value);
}

export function matchesSearch<T>(
  item: T,
  search: string,
  keys: (keyof T)[],
): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return keys.some((key) => {
    const value = item[key];
    return String(value ?? "")
      .toLowerCase()
      .includes(q);
  });
}

export function applySearch<T>(
  items: T[],
  search: string,
  keys: (keyof T)[],
): T[] {
  if (!search.trim()) return items;
  return items.filter((item) => matchesSearch(item, search, keys));
}

export function applyFilters<T>(
  items: T[],
  selected: Record<string, string[]>,
  accessor: FilterAccessor<T> = defaultFilterAccessor,
  predicates?: FilterPredicates<T>,
): T[] {
  const active = Object.entries(selected).filter(
    ([, values]) => values.length > 0,
  );
  if (active.length === 0) return items;
  return items.filter((item) =>
    active.every(([field, values]) => {
      const predicate = predicates?.[field];
      if (predicate) return predicate(item, values);
      const value = accessor(item, field);
      if (Array.isArray(value)) return value.some((v) => values.includes(v));
      return values.includes(value);
    }),
  );
}
